import React, { useState } from 'react';
import { FaPlus, FaRegWindowClose, FaFileImport, FaQuestionCircle, FaDownload } from 'react-icons/fa';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import { useTranslation, Trans } from 'react-i18next';


export const ProjectList = ({ projects, handleProjectSelect, handleMapUpdated }) => {
    const { t } = useTranslation();
    const [hideNewProjectDetails, setHideNewProjectDetails] = useState(true);
    const [newProjectName, setNewProjectName] = useState('');
    const [newProjectDescription, setNewProjectDescription] = useState('');
    const [selectedProject, setSelectedProject] = useState('');
    const [showHelpModal, setShowHelpModal] = useState(false);
    const [importError, setImportError] = useState('');

    const toggleNewProjectDetails = () => {
        setHideNewProjectDetails(!hideNewProjectDetails);
    }

    const handleNewProjectSubmit = (event) => {
        event.preventDefault();
        if (newProjectName === '') {
            return;
        }
        const newProject = {
            name: newProjectName,
            description: newProjectDescription
        };
        fetch('api/projects/newproject', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(newProject)
        })
            .then(response => response.json())
            .then(data => {
                console.log('Success:', JSON.stringify(data, null, 2));
                handleMapUpdated(data);
            });
        setNewProjectName('');
        setNewProjectDescription('');
        setHideNewProjectDetails(true);
    }

    const handleNewProjectNameChange = (event) => {
        setNewProjectName(event.target.value);
    }

    const handleNewProjectDescriptionChange = (event) => {
        setNewProjectDescription(event.target.value);
    }

    const handleSelectionChanged = (event) => {
        if (event.target.value === '') {
            return;
        }
        setSelectedProject(event.target.value);
        handleProjectSelect(event.target.value);
    }

    const handleDrillholeImport = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.csv';
        fileInput.onchange = (event) => {
            const file = event.target.files[0];
            if (file) {
                setImportError('');
                // Create a FormData object to send the file
                const formData = new FormData();
                formData.append('file', file);
                // Send the file to the server
                fetch('api/upload/drillholes', {
                    method: 'POST',
                    body: formData
                })
                    .then(async (response) => {
                        // The server returns plain-text on error, so read the body as text
                        // rather than assuming JSON (which produced a misleading parse error).
                        if (!response.ok) {
                            const errorText = await response.text();
                            const error = new Error(errorText || t('projects.importFailed'));
                            // Only a 400 is about the file itself. A 401 (session gone) or a 503
                            // (the customer's map could not be read, so nothing was imported) is
                            // not, and opening the format help for one sends the operator off to
                            // fix a file that was never the problem.
                            error.isFormatProblem = response.status === 400;
                            throw error;
                        }
                        return response.json();
                    })
                    .then(data => {
                        console.log('Import successful:', data);
                        setImportError('');
                        alert(data.message);
                        handleMapUpdated(data.map);
                    })
                    .catch(error => {
                        console.error('Error importing drillholes:', error);
                        // Surface the actual problem either way; only open the format help when
                        // the file is what the server objected to.
                        setImportError(error.message);
                        if (error.isFormatProblem) {
                            setShowHelpModal(true);
                        }
                    });
            }
        };
        fileInput.click();
    }

    const openHelp = () => {
        setImportError('');
        setShowHelpModal(true);
    }

    // The template's header row is what the server matches on, so it is English in every
    // language — as are the column names in the help below.
    const downloadTemplate = () => {
        const rows = [
            'Drillhole,Project,RigId,Latitude,Longitude,Elevation,Description,Planned Depth,Planned Azimuth,Planned Dip',
            'DH-001,Sample Project,RIG-12,-30.123456,121.654321,412.5,First example hole,500,135,-60',
            'DH-002,Sample Project,RIG-12,-30.124000,121.655000,413.1,Second example hole,480,140,-58'
        ];
        const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'drillhole_import_template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    let newProjectDetails = '';

    if (!hideNewProjectDetails) {
        newProjectDetails = (
            <div className="card">
                <div className="card-header">
                    <h5 className="card-title">{t('projects.newProject')}</h5>
                </div>
                <div className="card-body">
                    <form onSubmit={handleNewProjectSubmit}>
                        <div className="form-group">
                            <label htmlFor="projectName">{t('projects.projectName')}</label>
                            <input type="text" className="form-control" id="projectName" onChange={handleNewProjectNameChange} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="description">{t('projects.descriptionOptional')}</label>
                            <textarea className="form-control" id="description" rows="3" onChange={handleNewProjectDescriptionChange}></textarea>
                        </div>
                        <button type="submit" className="btn btn-secondary">{t('common.create')}</button>
                    </form>
                </div>
            </div>
        );
    }


    let projectListContents = '';

    if (projects && projects.length > 0) {
        projectListContents = (
            <select className="form-select form-select-sm" onChange={handleSelectionChanged} value={selectedProject}>
                <option value=''>{t('projects.selectProject')}</option>
                {projects.map((project, index) =>
                    <option key={project.name} value={project.name}>{project.name} {project.isCompleted && ' ✅'}</option>
                )}
            </select>
        );

    }

    return (
        <div>
            <h3>{t('projects.title')}</h3>
            <br />
            <div className="d-flex">
                <div className="btn-group" role="group">
                    <button className="btn btn-primary" onClick={() => handleDrillholeImport()}><FaFileImport /> {t('projects.importCsv')}</button>
                    <button className="btn btn-primary" title={t('projects.csvHelp')} aria-label={t('projects.csvHelp')} onClick={openHelp}><FaQuestionCircle /></button>
                </div>
                <button className="btn btn-primary ms-auto" onClick={toggleNewProjectDetails} >{hideNewProjectDetails ? <span><FaPlus /> {t('projects.newProject')}</span> : <span><FaRegWindowClose /> {t('common.cancel')}</span>}</button>
            </div>
            <br />
            {/* An import error that is not about the file format has no modal to live in, so it
                shows here. Dismissed by starting another import or opening the format help. */}
            {importError && !showHelpModal && (
                <div className="alert alert-danger" role="alert">{importError}</div>
            )}
            {newProjectDetails}
            {projectListContents}
            <Modal isOpen={showHelpModal} toggle={() => setShowHelpModal(false)} size="lg">
                <ModalHeader toggle={() => setShowHelpModal(false)}>{t('import.title')}</ModalHeader>
                <ModalBody>
                    {importError && (
                        <div className="alert alert-danger" role="alert">
                            {importError}
                        </div>
                    )}
                    <p><Trans i18nKey="import.intro" components={{ code: <code /> }} /></p>

                    <h6>{t('import.requiredHeading')}</h6>
                    <p>{t('import.requiredIntro')}</p>
                    <ul>
                        <li><strong>Drillhole</strong> — {t('import.columnDrillhole')}</li>
                        <li><strong>Project</strong> — {t('import.columnProject')}</li>
                    </ul>

                    <h6>{t('import.optionalHeading')}</h6>
                    <p>{t('import.optionalIntro')}</p>
                    <ul>
                        <li><strong>RigId</strong> — {t('import.columnRigId')}</li>
                        <li><strong>Latitude</strong> — {t('import.columnLatitude')}</li>
                        <li><strong>Longitude</strong> — {t('import.columnLongitude')}</li>
                        <li><strong>Elevation</strong> — {t('import.columnElevation')}</li>
                        <li><strong>Description</strong> — {t('import.columnDescription')}</li>
                    </ul>

                    <h6>{t('import.additionalHeading')}</h6>
                    <p><Trans i18nKey="import.additionalIntro" components={{ em: <em /> }} /></p>
                    <p>{t('import.plannedIntro')}</p>
                    <ul>
                        <li><strong>Planned Depth</strong> — {t('import.columnPlannedDepth')}</li>
                        <li><strong>Planned Azimuth</strong> — {t('import.columnPlannedAzimuth')}</li>
                        <li><strong>Planned Dip</strong> — {t('import.columnPlannedDip')}</li>
                    </ul>

                    <p className="text-muted mb-0">{t('import.skipped')}</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" onClick={downloadTemplate}><FaDownload /> {t('import.downloadTemplate')}</Button>
                    <Button color="secondary" onClick={() => setShowHelpModal(false)}>{t('common.close')}</Button>
                </ModalFooter>
            </Modal>
        </div>
    );
}
